import React, {useContext, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import {useStyles} from "./OrganizerDashboardSection.css";
import {AppbarContentContext} from "../../../../../commons/routing/appbar/AppbarContentProvider";
import {FormattedMessage, useIntl} from 'react-intl';
import useFetchOrganizerDashboardData from "./useFetchOrganizerDashboardData";
import LoadingProgressIndication from "../../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {IconButton, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import FormControl from "@mui/material/FormControl";
import noDashboardData from 'assets/noDashboardData.svg';
import {DateRangeSharp} from "@mui/icons-material";
import StatisticsDateRangePicker from "../../../statistics/utils/StatisticsDateRangePicker";
import {useConfirm} from "material-ui-confirm";
import {subDays} from 'date-fns';
import BasicChart from "../../../statistics/chart/BasicChart";
import ScoredScenesPerformanceList from "../../panels/performanceList/ScoredScenesPerformanceList";
import ValueIndicator from "../../panels/valueIndication/ValueIndicator";
import {NavLink} from "react-router-dom";
import {FirebaseAuthContext} from "../../../../../commons/firebase/FirebaseAuthProvider";
import noCoursesYet from "../../../../../../assets/undraw_graduation.svg";

export default function OrganizerDashboardSection() {
    const classes = useStyles();

    // Setting the current appbar content
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Confirmation dialog for selecting the time range
    const confirm = useConfirm();

    // Get permission-based limited association structure to fill the association filter dropdown
    const {allowedAssociationStructure} = useContext(FirebaseAuthContext);

    // Internationalization for simple strings
    const intl = useIntl();

    // Fetching the data required for rendering the statistics and retrieving selectable course/playlists
    const {
        fetchOrganizerDashboardData,
        selectableFilterHierarchy,
        noOwnedCourses
    } = useFetchOrganizerDashboardData()

    // Filter
    const [associationFilter, setAssociationFilter] = useState(Object.keys(allowedAssociationStructure)[0] || '')
    const [courseFilter, setCourseFilter] = useState('')
    const [playlistFilter, setPlaylistFilter] = useState('')
    const [fromToDateRange, setFromToDateRange] = React.useState([subDays(new Date(), 365), new Date()])

    // Selectable metrics for time chart and worst rated
    const [timeChartMetric, setTimeChartMetric] = React.useState('trainingIntensity');
    const [worstRatedMetric, setWorstRatedMetric] = React.useState('decisionAccuracy');

    // Raw dashboard data object as basis to render charts and numbers
    const [dashboardData, setDashboardData] = useState({})

    // Loading indication - set to false in case all charts are ready
    const [loadingStatistics, setLoadingStatistics] = useState(true)

    // Setting the appbar content including the filters
    useEffect(() => {
        setCurrentAppbarContent({
            header: <FormattedMessage id={'app.dashboard.overview.appbar.header'}
                                      defaultMessage={'Dashboard'}/>,
            description: <FormattedMessage id={'app.dashboard.overview.appbar.description'}
                                           defaultMessage={'All important KPIs at one glance...'}/>,
            actions: <>
                {getFilterSelection()}
            </>
        })
    }, [selectableFilterHierarchy, associationFilter, courseFilter, playlistFilter, fromToDateRange])

    // Load the dashboard data for the first time
    useEffect(() => {
        if (associationFilter && selectableFilterHierarchy.length > 0) {

            /* Reloading the dashboard for the first time - in order to avoid double-requests caused by multiple state changes,
               reloads on field changes are handled directly in the respective component's handler. */
            reloadDashboardData(associationFilter, courseFilter, playlistFilter, fromToDateRange[0], fromToDateRange[1])
        }
    }, [selectableFilterHierarchy])

    // Change of association resets course and playlist filter
    useEffect(() => {
        setCourseFilter('') // All
        setPlaylistFilter('') // All
    }, [selectableFilterHierarchy, associationFilter])

    // Change of course resets playlist filter
    useEffect(() => {
        setPlaylistFilter('') // All
    }, [selectableFilterHierarchy, associationFilter, courseFilter])

    // Reset tab configs to initial state after filter change
    useEffect(() => {
        setTimeChartMetric('trainingIntensity')
        setWorstRatedMetric('decisionAccuracy')
    }, [associationFilter, courseFilter, playlistFilter])

    const getSelectableCourses = () => {

        // Get courses in sorted order (as the courses array is pushed in arbitrary order by MongoDB)
        return selectableFilterHierarchy.find((a) => a.key === associationFilter)?.courses?.sort((c1, c2) => {
            if (c1.name < c2.name) {
                return -1
            }

            if (c1.name > c2.name) {
                return 1
            }

            return 0
        }) || []
    }

    const getSelectablePlaylists = () => {

        // Course filter is empty (i.e. "All Courses") - get playlists of all courses of the selected association
        if (courseFilter === '') {
            const playlists = []
            selectableFilterHierarchy.find((a) => a.key === associationFilter)?.courses?.forEach((c) => {
                c.playlists?.forEach((p) => {
                    if (!playlists.some((alreadyAddedPlaylist) => alreadyAddedPlaylist.key === p.key)) {
                        playlists.push(p)
                    }
                })
            })

            return playlists
        }

        // Only get the playlist of the selected course
        return selectableFilterHierarchy.find((a) => a.key === associationFilter)?.courses.find((c) => c.key === courseFilter)?.playlists || []
    }

    // Query statistical data and fill the chart canvas accordingly
    const reloadDashboardData = async (association, course, playlist, fromDate, toDate) => {
        setLoadingStatistics(true)

        // Load the basic statistical data as a basis for feeding the charts in the dashboard
        const dashboardData = await fetchOrganizerDashboardData(association, course, playlist, fromDate, toDate)

        // Set the dashboard data state, so that it can be used by the charts - go sure that the dashboard data is never set to null to avoid object access problems
        setDashboardData(dashboardData || {})

        setLoadingStatistics(false)
    }

    // Change of metric toggle button for line chart
    const handleChangeMetricForLineChart = (event, value) => {
        // Go sure that exactly one toggle button is always active
        if (value !== null) {
            setTimeChartMetric(value)
        }
    }

    // Change of metric toggle button for worst rated scored scenes
    const handleChangeMetricForWorstRatedScoredScenes = (event, value) => {
        // Go sure that exactly one toggle button is always active
        if (value !== null) {
            setWorstRatedMetric(value)
        }
    }

    const getFilterSelection = () => {

        // Hide dropdown in case the selectable items are not yet loaded
        if (!(selectableFilterHierarchy.length > 0)) {
            return <></>
        }

        // Filter row
        return <div className={classes.filterRow}>

            {/* Association */}
            <FormControl className={classes.formControl}>
                <Select id="reg-association-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        value={associationFilter || ''}
                        onChange={(event) => {
                            setAssociationFilter(event.target.value)
                            reloadDashboardData(event.target.value, '', '', fromToDateRange[0], fromToDateRange[1])
                        }}>

                    {Object.keys(allowedAssociationStructure).map((association) =>
                        <MenuItem key={association} value={association}>
                            {intl.formatMessage({
                                id: `app.ids.association.${association}`
                            })}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>

            {/* Course */}
            <FormControl className={classes.formControl}>
                <Select id="reg-course-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        displayEmpty={true}
                        value={courseFilter || ''}
                        onChange={(event) => {
                            setCourseFilter(event.target.value)
                            reloadDashboardData(associationFilter, event.target.value, '', fromToDateRange[0], fromToDateRange[1])
                        }}>

                    <MenuItem value="">
                        <em>All Courses</em>
                    </MenuItem>

                    {getSelectableCourses()?.map((course) =>
                        <MenuItem key={course.key} value={course.key}>
                            {course.name}
                        </MenuItem>)}
                </Select>
            </FormControl>

            {/* Playlist */}
            <FormControl className={classes.formControl}>
                <Select id="reg-playlist-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        displayEmpty={true}
                        value={playlistFilter || ''}
                        onChange={(event) => {
                            setPlaylistFilter(event.target.value)
                            reloadDashboardData(associationFilter, courseFilter, event.target.value, fromToDateRange[0], fromToDateRange[1])
                        }}>

                    <MenuItem value="">
                        <em>All Playlists</em>
                    </MenuItem>

                    {getSelectablePlaylists()?.map((playlist) =>
                        <MenuItem key={playlist.key} value={playlist.key}>
                            {playlist.name}
                        </MenuItem>)}
                </Select>
            </FormControl>

            {/* Date range */}
            <div style={{marginLeft: 10}}>
                <b>{intl.formatDate(fromToDateRange[0]) + ' - ' + intl.formatDate(fromToDateRange[1])}</b>
                <IconButton onClick={handlePickDate} size="large">
                    <DateRangeSharp/>
                </IconButton>
            </div>

        </div>
    }

    // Selection of time range
    const handlePickDate = () => {
        let selectedFromDate
        let selectedToDate

        confirm({
            title: intl.formatMessage({
                id: 'app.dashboard.dialog.dateRange.selection.title',
                defaultMessage: 'Choose time range...'
            }),
            content: <StatisticsDateRangePicker
                startDate={fromToDateRange[0]}
                endDate={fromToDateRange[1]}
                onPeriodChanged={(startDate, endDate) => {
                    selectedFromDate = startDate
                    selectedToDate = endDate
                }
                }/>
        }).then(() => {

            // IMPORTANT: When selecting nothing, but clicking simply on OK, selectedStartDate and selectedEndDate is undefined
            // IMPORTANT: the timestamps are always at "start date 00:00:00 UTC" - "end date 23:59:59 UTC"
            if (selectedFromDate && selectedToDate) {
                setFromToDateRange([selectedFromDate, selectedToDate])

                reloadDashboardData(associationFilter, courseFilter, playlistFilter, selectedFromDate, selectedToDate)
            }
        }).catch(() => null)
    }

    // Source: Idea from https://stackoverflow.com/questions/36098913/convert-seconds-to-days-hours-minutes-and-seconds
    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor(seconds % 3600 / 60)
        const s = Math.floor(seconds % 3600 % 60)

        const hh = h > 0 ? (h > 9 ? h : ("0" + h)) : "00"
        const mm = m > 0 ? (m > 9 ? m : ("0" + m)) : "00"
        const ss = s > 0 ? (s > 9 ? s : ("0" + s)) : "00"
        return `${hh}:${mm}:${ss}`
    }

    return (
        <div className={classes.root}>

            {/* Only hide the loading indication, so that the canvas-elements are always available in the DOM */}
            <div hidden={!loadingStatistics || noOwnedCourses}>
                <LoadingProgressIndication text={intl.formatMessage({
                    id: 'app.dashboard.loading',
                    defaultMessage: 'Loading Dashboard...'
                })}/>
            </div>

            {/* Hide complete section while loading and in case no statistics are generated yet */}
            <Grid container spacing={2}
                  sx={(loadingStatistics || dashboardData.noDataYet || noOwnedCourses) && {display: 'none'}}>

                {/* Overall indicators */}
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.decisionAccuracy',
                            defaultMessage: 'Correct Decisions'
                        })}
                        value={`${dashboardData.numberDecisionAccuracyTotal}%`}/>
                </Grid>
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.reactionTime',
                            defaultMessage: 'Reaction Time'
                        })}
                        value={dashboardData.numberReactionTimeTotal
                            ? `${dashboardData.numberReactionTimeTotal}s`
                            : '-'}/>
                </Grid>
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.trainingIntensity',
                            defaultMessage: 'Training Intensity'
                        })}
                        value={formatDuration(dashboardData.numberTrainingIntensityTotal * 60)}/>
                </Grid>

                {/* Average bar charts */}
                <Grid item xs={4}>
                    <BasicChart chartId="chartDecisionAccuracyAvgByDisciplines"
                                chartData={dashboardData.chartDecisionAccuracyAvg}/>
                </Grid>
                <Grid item xs={4}>
                    <BasicChart chartId="chartReactionTimeAvgByDisciplines"
                                chartData={dashboardData.chartReactionTimeAvg}/>
                </Grid>
                <Grid item xs={4}>
                    <BasicChart chartId="chartTrainingIntensityAbsByDisciplines"
                                chartData={dashboardData.chartTrainingIntensityAbs}/>
                </Grid>

                {/* Accumulated line charts with tab selection to choose metric */}
                <Grid item xs={5}>

                    <Paper className={classes.groupPaper}>

                        {/* Selection of the metric for the accumulated line chart (depending on that charts will be shown/hidden) */}
                        <ToggleButtonGroup className={classes.centeredRow}
                                           value={timeChartMetric}
                                           exclusive
                                           onChange={handleChangeMetricForLineChart}
                                           aria-label="chart-type">

                            <ToggleButton className={classes.toggleButton}
                                          value="trainingIntensity"
                                          aria-label="trainingIntensity">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.lineChart.metric.trainingIntensity',
                                    defaultMessage: 'Training Intensity'
                                })}
                            </ToggleButton>

                            <ToggleButton className={classes.toggleButton} value="decisionAccuracy"
                                          aria-label="decisionAccuracy">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.lineChart.metric.decisionAccuracy',
                                    defaultMessage: 'Decision Accuracy'
                                })}
                            </ToggleButton>

                            <ToggleButton className={classes.toggleButton} value="reactionTime"
                                          aria-label="reactionTime">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.lineChart.metric.reactionTime',
                                    defaultMessage: 'Reaction Time'
                                })}
                            </ToggleButton>

                        </ToggleButtonGroup>

                        {/* Accumulated line charts (only one of them is visible) */}
                        <div>
                            <div hidden={timeChartMetric !== 'trainingIntensity'}>
                                <BasicChart chartId="chartTrainingIntensityDevByDisciplines"
                                            chartData={dashboardData.chartTrainingIntensityDev}/>
                            </div>
                            <div hidden={timeChartMetric !== 'decisionAccuracy'}>
                                <BasicChart chartId="chartDecisionAccuracyDevByDisciplines"
                                            chartData={dashboardData.chartDecisionAccuracyDev}/>
                            </div>
                            <div hidden={timeChartMetric !== 'reactionTime'}>
                                <BasicChart chartId="chartReactionTimeDevByDisciplines"
                                            chartData={dashboardData.chartReactionTimeDev}/>
                            </div>
                        </div>

                    </Paper>

                </Grid>

                {/* Worst rated scored scenes (shown in an accordion, allowing to open only one item) */}
                <Grid item xs={5}>

                    <Paper className={classes.groupPaperWorstRated}>

                        {/* Selection of the metric for the worst rated scored scenes (depending on that lists will be shown/hidden) */}
                        <ToggleButtonGroup className={classes.centeredRow}
                                           value={worstRatedMetric}
                                           exclusive
                                           onChange={handleChangeMetricForWorstRatedScoredScenes}
                                           aria-label="worst-rated-scored-scenes">

                            <ToggleButton className={classes.toggleButton} value="decisionAccuracy"
                                          aria-label="decisionAccuracy">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.decisionAccuracy',
                                    defaultMessage: 'Decision Accuracy'
                                })}
                            </ToggleButton>

                            <ToggleButton className={classes.toggleButton} value="reactionTime"
                                          aria-label="reactionTime">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.reactionTime',
                                    defaultMessage: 'Reaction Time'
                                })}
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Worst rated list - decision accuracy */}
                        <div hidden={worstRatedMetric !== 'decisionAccuracy'}>
                            <ScoredScenesPerformanceList
                                metric="decisionAccuracy"
                                unit="%"
                                headerPreview={intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.decisionAccuracy.header',
                                    defaultMessage: 'Worst Decision Accuracy Video Scenes'
                                })}
                                listPreview={dashboardData.listWorstRatedScoredScenesDecisionAccuracy}
                                headerMore={intl.formatMessage({
                                    id: 'app.dashboard.dialog.dateRange.moreWorstRatedScenes.title',
                                    defaultMessage: 'Worst Rated - 25%'
                                })}
                                listMore={dashboardData.listWorstRatedScoredScenesDecisionAccuracyQ4}/>
                        </div>

                        {/* Worst rated list - reaction time */}
                        <div hidden={worstRatedMetric !== 'reactionTime'}>
                            <ScoredScenesPerformanceList
                                metric="reactionTime"
                                unit="s"
                                headerPreview={intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.reactionTime.header',
                                    defaultMessage: 'Worst Reaction Time Video Scenes'
                                })}
                                listPreview={dashboardData.listWorstRatedScoredScenesReactionTime}
                                headerMore={intl.formatMessage({
                                    id: 'app.dashboard.dialog.dateRange.moreWorstRatedScenes.title',
                                    defaultMessage: 'Worst Rated - 25%'
                                })}
                                listMore={dashboardData.listWorstRatedScoredScenesReactionTimeQ4}/>

                        </div>
                    </Paper>

                </Grid>

                {/* Challenged decisions (all open vs. open resolvable by signed-in user) */}
                <Grid item xs={2}>
                    <Paper className={classes.groupPaper50}>

                        {/* Header */}
                        <div className={classes.smallPanelHeader}>
                            <NavLink to={`/admin/challengedDecisions`}>
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.challengedDecisions.header',
                                    defaultMessage: 'Open Challenges'
                                })}
                            </NavLink>
                        </div>

                        {/* Number of total open challenged decisions of the selected association, where the signed-in user is the owner of the scored scene */}
                        <ValueIndicator
                            highlighted={true}
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.challengedDecisions.metric.open.assignedToYou',
                                defaultMessage: 'Assigned to you'
                            })}
                            value={dashboardData.numberOpenChallengedDecisionsResolvable}/>

                        {/* Number of total open challenged decisions of the selected association */}
                        <ValueIndicator
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.challengedDecisions.metric.open.total',
                                defaultMessage: 'Total'
                            })}
                            value={dashboardData.numberOpenChallengedDecisionsTotal}/>

                    </Paper>

                    {/* Rejected scored scenes (all rejected vs. rejected resolvable by signed-in user) */}
                    <Paper className={classes.groupPaper50}>

                        {/* Header */}
                        <div className={classes.smallPanelHeader}>
                            <NavLink to={`/admin/videos`}>
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.rejectedScoredScenes.header',
                                    defaultMessage: 'Rejected Scenes'
                                })}
                            </NavLink>
                        </div>

                        {/* Number of total open challenged decisions of the selected association, where the signed-in user is the owner of the scored scene */}
                        <ValueIndicator
                            highlighted={true}
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.rejectedScoredScenes.metric.open.assignedToYou',
                                defaultMessage: 'Assigned to you'
                            })}
                            value={dashboardData.numberRejectedScoredScenesResolvable}/>
                    </Paper>

                </Grid>
            </Grid>

            {/* In case no statistical data has been generated yet - show indication with nice cartoon image */}
            <div hidden={loadingStatistics || !dashboardData.noDataYet}>
                <Typography component="h1" variant="h4" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.header',
                        defaultMessage: 'Missing data to render dashboard...'
                    })}
                </Typography>
                <img className={classes.image} src={noDashboardData} alt='No dashboard data'/>
                <Typography component="h1" variant="h5" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.description',
                        defaultMessage: 'No activity for the requested filter criteria.'
                    })}
                </Typography>
            </div>

            {/* In case the user did not yet create (own) any course.
                Consider: loading is still set to true in this case, but the loading indication is hidden in case of noOwnedCourses. */}
            <div hidden={!noOwnedCourses}>
                <Typography component="h1" variant="h4" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noOwnedCourses.header',
                        defaultMessage: 'No courses created yet...'
                    })}
                </Typography>
                <img className={classes.image} src={noCoursesYet} alt='No owned course yet'/>
                <Typography component="h1" variant="h5" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noOwnedCourses.description',
                        defaultMessage: 'Start creating your first course.'
                    })}
                </Typography>
            </div>
        </div>
    );
}
